import React, { useState, useRef, useLayoutEffect } from 'react'
import styled from 'theme/styled-components'
import useTheme from 'theme/useTheme'
import Icons from '../icons/Icons'

interface Props {
  onOpen?: () => void
  onClose?: () => void
  children: React.ReactNode
  title: string
  duration?: number // Duration of the opening/closing animation
  notification?: boolean // Whether to show a notification dot before the title
}

const Dropdown = ({ onOpen, onClose, children, title, duration = 400, notification }: Props) => {
  const [theme] = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(0)

  const toggleExpanded = () => {
    if (isExpanded) {
      onClose?.()
    } else {
      onOpen?.()
    }
    setIsExpanded(!isExpanded)
  }

  useLayoutEffect(() => {
    const updateHeight = () => contentRef.current && setContentHeight(contentRef.current.scrollHeight)
    updateHeight()

    const observer = new MutationObserver(updateHeight)
    contentRef.current && observer.observe(contentRef.current, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
    }
  }, [children, isExpanded])

  return (
    <Container>
      <ToggleContainer onClick={toggleExpanded}>
        <TitleContainer>
          {notification && <NotificationDot />}
          <Title>{title}</Title>
        </TitleContainer>
        <Icon isExpanded={isExpanded} duration={duration}>
          <Icons name="chevron_bottom" size={20} color={theme.colors.blue} />
        </Icon>
      </ToggleContainer>
      <AnimatedContainer ref={contentRef} isExpanded={isExpanded} duration={duration} contentHeight={contentHeight}>
        <ContentContainer>{children}</ContentContainer>
      </AnimatedContainer>
      <SeparatorElement />
    </Container>
  )
}

export default Dropdown

const Container = styled.div``

const ToggleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  padding: 8px 0;
  cursor: pointer;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`

const NotificationDot = styled.span`
  background-color: ${(props) => props.theme.colors.blue};
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 10px;
`

const Title = styled.span`
  ${(props) => props.theme.fonts.bodyBold};
`

const Icon = styled.div<{ isExpanded: boolean; duration: number }>`
  transition: transform ${(props) => props.duration}ms;
  transform: rotate(${(props) => (props.isExpanded ? 180 : 0)}deg);
`

const AnimatedContainer = styled.div<{ isExpanded: boolean; duration: number; contentHeight: number }>`
  overflow: hidden;
  max-height: ${(props) => (props.isExpanded ? `${props.contentHeight}px` : '0')};
  transition: max-height ${(props) => props.duration}ms ease-in-out;
`

const ContentContainer = styled.div`
  padding: 6px 0;
  width: 100%;
`

const SeparatorElement = styled.div`
  margin-top: 4px;
  height: 1px;
  background-color: ${(props) => props.theme.colors.mediumLightGrey};
  width: 100%;
`
